import { useMemo } from 'react';
import useVersionManagement from './useVersionManagement';
import { parseDevelopmentGuidelines } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage development guidelines versions
 * Stores and tracks different versions of development guideline sections from regenerations
 */
const useDevelopmentGuidelinesVersions = (conversation, isStreaming, streamingContent) => {
  // Configuration for development guidelines version management
  const developmentGuidelinesConfig = {
    // Determine if a message contains development guidelines content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // Check for H3 headers (sections) and H2 headers (categories) with development guidelines keywords
      return (content.includes('### ') || content.includes('## ')) && (
        content.includes('Development') ||
        content.includes('Code') ||
        content.includes('Coding') ||
        content.includes('Standards') ||
        content.includes('Best Practices') ||
        content.includes('Guidelines') ||
        content.includes('Principles') ||
        content.includes('SOLID') ||
        content.includes('Design Pattern') ||
        content.includes('Testing') ||
        content.includes('Documentation') ||
        content.includes('Convention') ||
        content.includes('Style Guide')
      );
    },

    // Parse development guidelines sections from content
    // Uses H2 (##) for categories and H3 (###) for sections
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseDevelopmentGuidelines(content);
    },

    // Extract version information from a message
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      return {
        id: msg._id || msg.id || `${msg.createdAt}-${versionNumber}`,
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: parsedSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent
  };

  // Use the generic version management hook
  const versionData = useVersionManagement(developmentGuidelinesConfig);

  // Memoize currentVersion to prevent unnecessary re-renders
  const memoizedCurrentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;

    return {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    };
  }, [versionData.currentVersion, versionData.currentVersionIndex]);

  // Map the generic version data to development guidelines-specific structure
  return {
    versions: versionData.versions,
    currentVersion: memoizedCurrentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useDevelopmentGuidelinesVersions;
